"use client"

import React, { Component, ReactNode } from 'react'
import { ErrorDisplay } from './ErrorDisplay'
import { ERROR_MESSAGES, createErrorFromMessage } from '@/lib/utils/error-migration-helper'
import { logger } from '@/lib/services/logger.service'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error?: Error
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error to error reporting service in production
    if (process.env.NODE_ENV === 'production') {
      // TODO: Send to error reporting service
      logger.error('React component error caught by boundary', error, {
        component: 'ErrorBoundary',
        action: 'componentDidCatch',
        errorInfo: {
          componentStack: errorInfo.componentStack
        },
        errorMessage: error?.message,
        errorName: error?.name
      })
    }
  }

  handleReset = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-gradient-to-b from-primary-50 to-white flex items-center justify-center p-4">
          <div className="max-w-md w-full">
            <ErrorDisplay
              error={createErrorFromMessage('system/unexpected', {
                action: {
                  label: 'Try again',
                  onClick: this.handleReset
                }
              })}
            />
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Hook version for functional components
export function useErrorHandler() {
  const [error, setError] = React.useState<Error | null>(null)

  React.useEffect(() => {
    if (error) {
      throw error
    }
  }, [error])

  return setError
}